import { useCallback, useEffect, useState } from 'react';
import { api, setViewingCompany, type Adapter, type Vehicle } from './lib/api.ts';
import { useScanner } from './lib/useScanner.ts';
import { ConnectionPanel } from './components/ConnectionPanel.tsx';
import { AdapterSetup } from './components/AdapterSetup.tsx';
import { AdapterSettings } from './components/AdapterSettings.tsx';
import { ScanView } from './components/ScanView.tsx';
import { PartsPanel } from './components/PartsPanel.tsx';
import { HistoryView } from './components/HistoryView.tsx';
import { Terminal } from './components/Terminal.tsx';
import { FleetPanel } from './components/FleetPanel.tsx';
import { AuthScreen } from './components/AuthScreen.tsx';
import { MarketingPage } from './components/MarketingPage.tsx';
import { ensureDeviceGranted } from './lib/transports/webbluetooth.ts';
import type { Mode22Probe, ModulePresence } from './lib/obd/mode22.ts';

// The dev bridge keeps the unclaimed bootstrap; a deployed one does not.
const IS_PRODUCTION = !['localhost', '127.0.0.1'].includes(window.location.hostname);
import { PeoplePanel } from './components/PeoplePanel.tsx';
import { PlatformPanel } from './components/PlatformPanel.tsx';
import { PlatformScans } from './components/PlatformScans.tsx';
import { WhoAmI } from './components/WhoAmI.tsx';
import { ConnectivityBanner } from './components/ConnectivityBanner.tsx';
import { SCENARIOS } from './lib/transports/simulator.ts';
import { allDtcs } from './lib/elm327.ts';
import { DATE_FORMATS, PreferencesProvider, usePrefs, type DateFormat } from './lib/prefs.tsx';

type Tab = 'scan' | 'history' | 'log' | 'people' | 'platform';

/** Preferences live in context, so the shell just wraps the app in the provider. */
// Matches the prefs namespace in lib/prefs.tsx.
const ADAPTER_KEY = 'bayone.adapter';
const SIDEBAR_KEY = 'bayone.sidebar-width';

// Wide enough for a vehicle name and its flags, narrow enough to leave the
// content column usable on a laptop.
const SIDEBAR_MIN = 240;
const SIDEBAR_MAX = 520;

function storedSidebarWidth(): number {
  try {
    const n = Number(localStorage.getItem(SIDEBAR_KEY));
    return Number.isFinite(n) && n >= SIDEBAR_MIN && n <= SIDEBAR_MAX ? n : 320;
  } catch {
    return 320;
  }
}

/**
 * The chosen adapter survives a reload, on an instance nobody has claimed.
 *
 * A signed-in person's choice lives on their account, so it follows them to
 * another machine. The unclaimed development bridge has no account to hold it,
 * and this is where it goes instead. Storage access throws rather than
 * returning null in some private-browsing modes, hence the guards.
 */
function rememberAdapter(id: number): void {
  try {
    localStorage.setItem(ADAPTER_KEY, String(id));
  } catch {
    // Storage unavailable; the selection just will not persist.
  }
}

function recallAdapter(): number | null {
  try {
    const raw = localStorage.getItem(ADAPTER_KEY);
    const id = raw === null ? NaN : Number(raw);
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

export function App() {
  return (
    <PreferencesProvider>
      <Shell />
    </PreferencesProvider>
  );
}

function Shell() {
  const [adapters, setAdapters] = useState<Adapter[]>([]);
  // False until the first list arrives, so setup does not open over an empty
  // list that is really a list still loading.
  const [adaptersLoaded, setAdaptersLoaded] = useState(false);
  const [selectedAdapter, setSelectedAdapter] = useState<Adapter | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  // Set when somebody who cannot add adapters closes setup, so it does not
  // reopen on every render. Pressing Set up adapter brings it back.
  const [setupDismissed, setSetupDismissed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Which vehicle the history view is showing. Lifted here so the sidebar can
  // drive it and the two lists become one.
  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  // null while unknown, so the app does not flash a sign-in screen at someone
  // who is already signed in.
  const [auth, setAuth] = useState<Awaited<ReturnType<typeof api.authState>> | null>(null);

  const loadAuth = useCallback(() => api.authState().then(setAuth).catch(() => setAuth(null)), []);
  useEffect(() => {
    void loadAuth();
  }, [loadAuth]);
  const [sidebarWidth, setSidebarWidth] = useState(storedSidebarWidth);

  // Dragging writes straight to the CSS variable and only stores the result on
  // release, so a drag is one style write per frame rather than a React render
  // per pixel.
  const startResize = useCallback((startEvent: React.PointerEvent) => {
    startEvent.preventDefault();
    const startX = startEvent.clientX;
    const startWidth = sidebarWidth;

    const move = (e: PointerEvent) => {
      const next = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startWidth + (e.clientX - startX)));
      document.documentElement.style.setProperty('--sidebar-w', `${next}px`);
    };

    const done = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', done);
      document.body.style.userSelect = '';
      const applied = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w'), 10);
      if (Number.isFinite(applied)) {
        setSidebarWidth(applied);
        try {
          localStorage.setItem(SIDEBAR_KEY, String(applied));
        } catch {
          // Storage unavailable; the width just will not persist.
        }
      }
    };

    // Without this a drag selects the text it passes over.
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', done);
  }, [sidebarWidth]);

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', `${sidebarWidth}px`);
  }, [sidebarWidth]);
  const [simScenario, setSimScenario] = useState(SCENARIOS[0].id);
  // History is the landing surface: it holds the vehicle, its recalls,
  // maintenance and every scan including the newest. The scan tab is where a
  // result goes only when no VIN came back and there is no vehicle to open.
  const [tab, setTab] = useState<Tab>('history');
  const [showAuth, setShowAuth] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  // Another company a platform administrator is reading, read-only. Everything
  // below shows that company while this is set, because every request carries
  // it; leaving puts them back on the platform screen they came from.
  const [viewing, setViewing] = useState<{ id: number; name: string } | null>(null);
  const viewCompany = useCallback((target: { id: number; name: string } | null, vin: string | null = null) => {
    // Your own company is not somewhere to visit read-only. It is just home.
    const away = target && target.id !== auth?.companyId ? target : null;
    setViewingCompany(away?.id ?? null);
    setViewing(away);
    setSelectedVin(vin);
    setHistoryKey((k) => k + 1);
    setTab(target ? 'history' : 'platform');
  }, [auth?.companyId]);
  const [bridgeDown, setBridgeDown] = useState(false);
  const { units, setUnits, dateFormat, setDateFormat } = usePrefs();

  const scanner = useScanner();

  // Picking a different adapter must drop the current connection. Without this
  // the status pill keeps reporting the previous transport's state, so choosing
  // a Bluetooth reader while joined over Wi-Fi claims a connection that does
  // not exist for the adapter now on screen.
  //
  // Choosing is also saving: the adapter you pick is yours until you pick
  // another, on any machine you sign in from.
  const userId = auth?.userId ?? null;
  const selectAdapter = useCallback(
    (adapter: Adapter) => {
      setSelectedAdapter((current) => {
        if (current && current.id !== adapter.id) void scanner.disconnect();
        return adapter;
      });
      rememberAdapter(adapter.id);
      if (userId) {
        setAuth((a) => (a ? { ...a, defaultAdapterId: adapter.id } : a));
        void api.setMyAdapter(adapter.id).catch(() => undefined);
      }
    },
    [scanner, userId],
  );

  const loadAdapters = useCallback(async () => {
    try {
      const list = await api.listAdapters();
      setAdapters(list);
      setAdaptersLoaded(true);
      setBridgeDown(false);
    } catch (err) {
      // A 401 means signed out and a 403 means this account may not have it.
      // Neither is a bridge that is down, and saying so sends somebody to
      // restart a server that is running perfectly - or, for a customer looking
      // at their own car, tells them to run npm run dev.
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        void loadAuth();
        return;
      }
      if (status === 403) {
        // Customers cannot reach adapters at all. There is nothing to select
        // and nothing wrong.
        setAdapters([]);
        setAdaptersLoaded(true);
        setBridgeDown(false);
        return;
      }
      setBridgeDown(true);
    }
  }, []);

  useEffect(() => {
    void loadAdapters();
  }, [loadAdapters]);

  /**
   * Which adapter this person scans with.
   *
   * Their own choice if it still exists. There is no fallback to the first in
   * the list for a signed-in person: picking one for them is exactly what hid
   * the fact that nothing had been set up. The unclaimed development bridge
   * keeps the old fallback, since it has no account to ask.
   */
  const preferredAdapterId = auth?.userId ? auth.defaultAdapterId : recallAdapter();
  const preferredAdapter =
    adapters.find((a) => a.id === preferredAdapterId) ??
    (auth && !auth.userId ? (adapters.find((a) => a.isDefault) ?? adapters[0] ?? null) : null);

  // The choice used to live in this browser only. When the account gained one,
  // existing staff were given their company's default, which is not
  // necessarily the reader this browser had been using. Carry the browser's
  // choice across once, so nobody's adapter changes under them.
  useEffect(() => {
    if (!auth?.userId || !adaptersLoaded) return;
    try {
      if (localStorage.getItem(`${ADAPTER_KEY}.moved`)) return;
      localStorage.setItem(`${ADAPTER_KEY}.moved`, '1');
    } catch {
      return;
    }
    const remembered = adapters.find((a) => a.id === recallAdapter());
    if (remembered && remembered.id !== auth.defaultAdapterId) selectAdapter(remembered);
  }, [auth?.userId, auth?.defaultAdapterId, adapters, adaptersLoaded, selectAdapter]);

  useEffect(() => {
    if (!auth) return;
    setSelectedAdapter((current) => {
      const still = current && adapters.find((a) => a.id === current.id);
      return still || preferredAdapter;
    });
  }, [adapters, auth, preferredAdapter]);

  // Customers never scan, and a platform administrator reading another company
  // cannot scan into it. Everyone else needs an adapter before Scan means
  // anything. Viewers can pick one but not add one.
  const canScan = Boolean(auth) && auth?.role !== 'customer' && !viewing && !bridgeDown;
  const canWriteAdapters = !auth?.signedIn || auth?.role === 'owner' || auth?.role === 'technician';
  const needsSetup = canScan && adaptersLoaded && !preferredAdapter && !selectedAdapter;

  useEffect(() => {
    if (needsSetup && !setupDismissed) setSetupOpen(true);
  }, [needsSetup, setupDismissed]);

  // Keep the vehicle record beside the scan so parts lookups can be
  // year/make/model aware.
  useEffect(() => {
    const vin = scanner.result?.vin;
    if (!vin) {
      setVehicle(null);
      return;
    }
    api.getVehicle(vin).then(setVehicle).catch(() => setVehicle(null));
  }, [scanner.result?.vin, scanner.savedScanId]);

  // Reported, never stored. Until one of these matches a dashboard, every
  // number here is a candidate and writing one to the record would be
  // inventing mileage.
  const [odometer, setOdometer] = useState<{ busy: boolean; rows: Mode22Probe[]; error: string | null }>({
    busy: false, rows: [], error: null,
  });

  const [modules, setModules] = useState<{ busy: boolean; rows: ModulePresence[]; error: string | null }>({
    busy: false, rows: [], error: null,
  });

  async function handleModules() {
    if (!selectedAdapter) return;
    if (selectedAdapter.transport === 'ble') {
      try {
        await ensureDeviceGranted();
      } catch (err) {
        if ((err as Error).name !== 'NotFoundError') throw err;
        return;
      }
    }
    setModules({ busy: true, rows: [], error: null });
    try {
      setModules({ busy: false, rows: await scanner.probeModules(selectedAdapter), error: null });
    } catch (err) {
      setModules({ busy: false, rows: [], error: (err as Error).message });
    }
  }

  async function handleOdometer() {
    if (!selectedAdapter) return;
    if (selectedAdapter.transport === 'ble') {
      try {
        await ensureDeviceGranted();
      } catch (err) {
        if ((err as Error).name !== 'NotFoundError') throw err;
        return;
      }
    }
    setOdometer({ busy: true, rows: [], error: null });
    try {
      const rows = await scanner.probeOdometer(selectedAdapter);
      setOdometer({ busy: false, rows, error: null });
    } catch (err) {
      setOdometer({ busy: false, rows: [], error: (err as Error).message });
    }
  }

  async function handleScan() {
    if (!selectedAdapter) {
      setSetupOpen(true);
      return;
    }

    // Before anything else that awaits. The chooser needs the user gesture from
    // the click that got us here, and every await between the two spends it.
    if (selectedAdapter.transport === 'ble') {
      try {
        await ensureDeviceGranted();
      } catch (err) {
        // A cancelled chooser is a decision, not a failure worth a banner.
        if ((err as Error).name !== 'NotFoundError') throw err;
        return;
      }
    }

    setSelectedCode(null);
    const result = await scanner.scan(selectedAdapter, simScenario);
    if (!result) return;

    setHistoryKey((k) => k + 1);

    // The VIN is only known once the scan has run, which is why selection
    // cannot happen before it. Now that it has, attach the result to the
    // vehicle it belongs to - creating the record or joining an existing one -
    // and show it in that vehicle's history rather than leaving the scan
    // stranded in a tab of its own.
    // The scan is already saved and the history list refreshes above, so the
    // vehicle view is where the result lives. Falling back to the scan surface
    // only matters when no VIN came back and there is no vehicle to open.
    if (result.vin) {
      setSelectedVin(result.vin);
      setTab('history');
    } else {
      setTab('scan');
    }
  }

  // Confirmed in the connection panel, next to the button and the code count,
  // so by the time this runs the answer is already yes.
  async function handleClearCodes() {
    const ok = await scanner.clearCodes();
    if (ok && selectedAdapter) {
      // Re-scan so the screen shows what the vehicle reports now, not what it
      // reported before the clear.
      await scanner.scan(selectedAdapter, simScenario);
      setHistoryKey((k) => k + 1);
    }
  }

  const codeCount = scanner.result ? allDtcs(scanner.result).length : 0;

  // An unclaimed production instance shows the screen too - it is how the first
  // account gets made. Only a development instance skips it, where the
  // bootstrap actor still stands in for one.
  if (auth && !auth.signedIn && (auth.unclaimed ? IS_PRODUCTION : true)) {
    // The marketing page first, with sign-in a click away. Opening on a
    // sign-in card asks somebody to prove who they are before telling them
    // what this is, and gives a member sent a link nothing to read.
    //
    // An unclaimed instance skips it: there is nobody to market to yet, and
    // the only useful thing to do is claim it. An invite link skips it too -
    // they were sent somewhere specific and should land there.
    if (!showAuth && !auth.unclaimed && !new URLSearchParams(window.location.search).has('invite')) {
      return <MarketingPage onSignIn={() => setShowAuth(true)} />;
    }
    return <AuthScreen onSignedIn={() => window.location.reload()} unclaimed={auth.unclaimed} />;
  }

  return (
    <div className="app">
      <header className="header">
        <div className="brand">
          {/* Mark and wordmark are one badge, not two boxes side by side. The
              mark has no field of its own so it takes the badge's green, which
              differs between the light and dark themes. */}
          {/* One lockup: the filled badge carries the mark and the company, and
              the border continues out of it around the product name, so the two
              read as a single object rather than a badge with a word beside it. */}
          <span className="brand-lockup">
            <span className="brand-mark">
              <img className="brand-logo" src="/logo-mark.png" alt="" width={20} height={20} />
              Concourse Works
            </span>
            <span className="brand-tool">Bay One</span>
          </span>
          {/* The company owns the vehicles and the scans, so whose data is on
              screen is worth stating rather than inferring from the fleet. */}
          <small>{auth?.company ?? 'OBD-II scan tool'}</small>
        </div>
        <div className="header-spacer" />
        {scanner.result?.vin && <span className="pill mono">{scanner.result.vin}</span>}


        {auth?.signedIn && (
          <div className="row" style={{ gap: 6 }}>
            {/* The name is who you are; the role is what you can do. Both are
                worth seeing at a glance on a shared machine in a workshop,
                where signing out and back in is routine. */}
            <WhoAmI
              name={auth.name} email={auth.email} role={auth.role}
              firstName={auth.firstName} lastName={auth.lastName} username={auth.username}
            />
            <button
              className="tiny ghost"
              onClick={() => api.logout().then(() => window.location.reload())}
              title={`Signed in as ${auth.email}`}
            >
              Sign out
            </button>
          </div>
        )}

        <div className="unit-switch" role="group" aria-label="Measurement units">
          <button aria-pressed={units === 'metric'} onClick={() => setUnits('metric')} title="Celsius and km/h">
            °C
          </button>
          <button aria-pressed={units === 'imperial'} onClick={() => setUnits('imperial')} title="Fahrenheit and mph">
            °F
          </button>
        </div>

        <select
          className="date-pref"
          aria-label="Date format"
          value={dateFormat}
          onChange={(e) => setDateFormat(e.target.value as DateFormat)}
          title="How dates are displayed"
        >
          {DATE_FORMATS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label} · {f.example}
            </option>
          ))}
        </select>
      </header>

      <aside className="sidebar">
        {/* A customer cannot scan and cannot reach an adapter, so the whole
            connection panel is meaningless to them. Showing it empty invites
            them to wonder what they are missing; showing what they do have
            answers the question they actually arrived with. */}
        {viewing ? (
          <div className="customer-note small dim">
            Scanning is off while you are reading {viewing.name}. Anything you scanned now would be filed under
            your own company, not theirs, so go back to your company first.
          </div>
        ) : auth?.role === 'customer' ? (
          <div className="customer-note small dim">
            Your vehicles are below. Scan history, open recalls and upcoming service are recorded by whoever is
            looking after them.
          </div>
        ) : bridgeDown ? (
          <div className="banner error small">
            The bridge is not responding on this origin. Start it with <span className="mono">npm run dev</span> from
            the project root.
          </div>
        ) : (
          <ConnectionPanel
            selected={selectedAdapter}
            onOpenSettings={() => setSettingsOpen(true)}
            onOpenSetup={() => setSetupOpen(true)}
            busy={scanner.busy}
            simScenario={simScenario}
            onSimScenario={setSimScenario}
            status={scanner.status}
            holdSecondsLeft={scanner.holdSecondsLeft}
            progress={scanner.progress}
            onScan={handleScan}
            onDisconnect={scanner.disconnect}
            onClearCodes={handleClearCodes}
            storedCodeCount={scanner.result?.stored.length ?? 0}
            live={scanner.live}
            onToggleLive={scanner.result ? (scanner.live ? scanner.stopLive : scanner.startLive) : undefined}
          />
        )}

        {/* Below the adapter controls, which matter for about ten seconds a
            session. This column was otherwise empty on every tab but Scan. */}
        <FleetPanel
          refreshKey={historyKey}
          selected={selectedVin}
          onSelect={(vin) => {
            setSelectedVin(vin);
            setTab('history');
          }}
        />
        {/* Keyboard users get the arrows; the pointer gets the drag. */}
        <div
          className="sidebar-grip"
          role="separator"
          aria-label="Resize sidebar"
          aria-orientation="vertical"
          aria-valuenow={sidebarWidth}
          aria-valuemin={SIDEBAR_MIN}
          aria-valuemax={SIDEBAR_MAX}
          tabIndex={0}
          onPointerDown={startResize}
          onDoubleClick={() => setSidebarWidth(320)}
          onKeyDown={(e) => {
            const step = e.shiftKey ? 40 : 16;
            if (e.key === 'ArrowLeft') setSidebarWidth((w) => Math.max(SIDEBAR_MIN, w - step));
            if (e.key === 'ArrowRight') setSidebarWidth((w) => Math.min(SIDEBAR_MAX, w + step));
          }}
          title="Drag to resize, double-click to reset"
        />
      </aside>

      <main className="main">
        <div className="tabs" role="tablist">
          {/* No Scan tab. A scan result is a history entry, and it is shown
              inside the vehicle it belongs to. The scan surface below is still
              reachable, but only when a scan reports no VIN and there is no
              vehicle entry to host the result - so it is offered when it is the
              only place to look, rather than sitting there as a second copy of
              what history already shows. */}
          {tab === 'scan' && (
            <button role="tab" aria-selected onClick={() => setTab('scan')}>
              Unmatched scan
              {codeCount > 0 && <span className="count">{codeCount}</span>}
            </button>
          )}
          <button role="tab" aria-selected={tab === 'history'} onClick={() => setTab('history')}>
            History
          </button>
          {/* A raw ELM327 transcript and the diagnostic probes below it. Useful
              to whoever is holding the adapter, meaningless to the owner of the
              car - who cannot connect one in the first place. */}
          {auth?.role !== 'customer' && (
            <button role="tab" aria-selected={tab === 'log'} onClick={() => setTab('log')}>
              Adapter log
              {scanner.log.length > 0 && <span className="count">{scanner.log.length}</span>}
            </button>
          )}
          {/* Hidden rather than shown-and-failing. The server refuses the
              routes to anyone but an owner regardless. */}
          {auth?.role === 'owner' && !viewing && (
            <button role="tab" aria-selected={tab === 'people'} onClick={() => setTab('people')}>
              People
            </button>
          )}
          {/* Hidden unless the account administers the platform. The server
              refuses /api/platform/ regardless, which is the half that counts. */}
          {auth?.platformAdmin && (
            <button role="tab" aria-selected={tab === 'platform'} onClick={() => setTab('platform')}>
              Platform
            </button>
          )}
        </div>

        {viewing && (
          <div className="banner viewing-banner" style={{ marginBottom: 16 }}>
            <span>
              Viewing <strong>{viewing.name}</strong> as they see it. Read-only: nothing here can be changed.
            </span>
            <button className="tiny" onClick={() => viewCompany(null)}>
              back to your company
            </button>
          </div>
        )}

        <ConnectivityBanner onEnriched={() => setHistoryKey((k) => k + 1)} />

        {scanner.error && (
          <div className="banner error" style={{ marginBottom: 16 }}>
            {scanner.error}
          </div>
        )}

        {tab === 'scan' && (
          <>
            <ScanView
              result={scanner.result}
              selectedCode={selectedCode}
              onSelectCode={setSelectedCode}
              savedScanId={scanner.savedScanId}
              live={scanner.live}
              liveKeys={scanner.liveKeys}
              samples={scanner.samples}
              liveHz={scanner.liveHz}
              onToggleLiveKey={scanner.toggleLiveKey}
              units={units}
            />
            <PartsPanel code={selectedCode} vehicle={vehicle} />
          </>
        )}

        {tab === 'history' && <HistoryView
                refreshKey={historyKey}
                selectedVin={selectedVin}
                isOwner={auth?.role === 'owner' && !viewing}
                isCustomer={auth?.role === 'customer'}
                live={scanner.live}
                liveKeys={scanner.liveKeys}
                samples={scanner.samples}
                liveHz={scanner.liveHz}
                onToggleLiveKey={scanner.toggleLiveKey}
                liveScanId={scanner.savedScanId}
              />}

        {tab === 'people' && <PeoplePanel />}
        {tab === 'platform' && (
          <div className="stack" style={{ gap: 16 }}>
            <PlatformPanel you={auth?.userId ?? null} onView={(c) => viewCompany(c)} />
            <PlatformScans onOpen={(s) => viewCompany({ id: s.companyId, name: s.company }, s.vin)} />
          </div>
        )}


        {tab === 'log' && (
          <>
            {/* Lives on the adapter log because that is where somebody goes
                when they are working out what a vehicle will actually answer.
                It is a diagnostic, not a feature of a scan. */}
            <div className="card">
              <h2>Odometer probe</h2>
              <p className="small dim">
                Asks each Ford module for the identifiers that might hold mileage. PID 0xA6 is almost never supported,
                so this reads manufacturer data instead — and the identifiers are candidates, not facts. Nothing is
                saved. Compare what comes back with the dashboard: whichever row matches is the real one.
              </p>
              <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
                <button disabled={!selectedAdapter || odometer.busy} onClick={handleOdometer}>
                  {odometer.busy ? 'Reading…' : 'Read odometer'}
                </button>
                {selectedAdapter && <span className="small dim">via {selectedAdapter.name}</span>}
              </div>

              {odometer.error && <div className="banner error small" style={{ marginTop: 10 }}>{odometer.error}</div>}

              {!odometer.busy && odometer.rows.length === 0 && !odometer.error && (
                <p className="small dim" style={{ marginTop: 10 }}>
                  Nothing read yet. Note the dashboard reading before you start.
                </p>
              )}

              {odometer.rows.length > 0 && (
                <div className="scroller" style={{ marginTop: 12 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Module</th><th>Identifier</th><th>Raw</th><th>As km</th><th>As miles</th><th>Other scalings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {odometer.rows.map((r) => (
                        <tr key={`${r.header}-${r.did}`}>
                          <td>{r.moduleName}<span className="dim mono small"> {r.header}</span></td>
                          <td className="mono">{r.did}</td>
                          <td className="mono">{r.raw ?? '—'}</td>
                          <td className="mono">{r.asKm?.toLocaleString() ?? '—'}</td>
                          <td className="mono">{r.asMiles?.toLocaleString() ?? '—'}</td>
                          <td className="mono small">
                            {r.scaled.map((x) => `×${x.factor}=${x.km.toLocaleString()}`).join('  ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <h2>Module presence</h2>
              <p className="small dim">
                Knocks once on every known address and reports who is home. A module that refuses is present and
                simply does not support what was asked; one that is silent is not on this bus at all, and no
                identifier will change that. Worth running before hunting for one.
              </p>
              <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
                <button disabled={!selectedAdapter || modules.busy} onClick={handleModules}>
                  {modules.busy ? 'Knocking…' : 'Sweep modules'}
                </button>
              </div>

              {modules.error && <div className="banner error small" style={{ marginTop: 10 }}>{modules.error}</div>}

              {modules.rows.length > 0 && (
                <div className="scroller" style={{ marginTop: 12 }}>
                  <table className="data">
                    <thead><tr><th>Address</th><th>Module</th><th>Present</th><th>Mode 22</th><th>Reply</th></tr></thead>
                    <tbody>
                      {modules.rows.map((m) => (
                        <tr key={m.header}>
                          <td className="mono">{m.header}</td>
                          <td>{m.name}</td>
                          <td>
                            <span className={`pill ${m.state === 'answered' ? '' : 'crit'}`}>
                              {m.state === 'answered' ? 'present' : 'nobody home'}
                            </span>
                          </td>
                          <td>
                            {m.state === 'answered' && (
                              <span className={`pill ${m.mode22 === 'yes' ? '' : m.mode22 === 'other-identifier' ? 'warn' : 'crit'}`}>
                                {m.mode22 === 'yes' ? 'yes' : m.mode22 === 'other-identifier' ? 'try another id' : m.mode22 === 'no' ? 'unsupported' : 'no reply'}
                              </span>
                            )}
                          </td>
                          <td className="mono small">{m.raw ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Terminal log={scanner.log} />
          </>
        )}
      </main>

      {setupOpen && canScan && (
        <AdapterSetup
          adapters={adapters}
          company={auth?.company ?? null}
          canWrite={canWriteAdapters}
          onAdaptersChanged={loadAdapters}
          onChosen={(adapter) => {
            selectAdapter(adapter);
            setSetupOpen(false);
          }}
          // Somebody who can add an adapter finishes setup, or takes the
          // simulator. A viewer may have nothing to choose, so they can leave.
          onClose={
            canWriteAdapters
              ? undefined
              : () => {
                  setSetupOpen(false);
                  setSetupDismissed(true);
                }
          }
        />
      )}

      {settingsOpen && canScan && (
        <AdapterSettings
          adapters={adapters}
          selected={selectedAdapter}
          canWrite={canWriteAdapters}
          onSelect={selectAdapter}
          onAdaptersChanged={loadAdapters}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}
